import React, { useState, useEffect } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { apiService } from '../../constants/ApiService';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import { useSelector } from 'react-redux';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { getMaxDate } from '../../utils/helperFuncation';

const Fee_Collection_DateWise_Report = ({ systemData }) => {
  const sessionData = useSelector((state) => state.sessionState);
  const { user } = useSelector((state) => state.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const [classMasterData, setClassMasterData] = useState([]);
  const [curr_class, setcurr_class] = useState('');

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const [fromDate, setFromDate] = useState(
    sessionData.session_start_date || formatDate(yesterday)
  );
  const [toDate, setToDate] = useState(
    getMaxDate(sessionData.session_end_date, sessionData.currentDate) ||
      formatDate(today)
  );

  useEffect(() => {
    setFromDate(sessionData.session_start_date || formatDate(yesterday));
    setToDate(
      getMaxDate(sessionData.session_end_date, sessionData.currentDate) ||
        formatDate(today)
    );
  }, [sessionData]);

  // =====================================================
  // FETCH CLASS DROPDOWN
  // =====================================================

  const GetCurrectClassInfo = async (search = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      const response = await apiService.get(
         `api/ClassMaster/GetClassMaster?vendorId=${vendorId}&branchId=${branchId}&searchItem=${encodeURIComponent(
          search,
        )}`);
      setClassMasterData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });
    if (canLoad) {
      GetCurrectClassInfo();
    }
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // =====================================================
  // FORMAT DATE HELPER
  // =====================================================

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  // =====================================================
  // DOWNLOAD REPORT
  // =====================================================

  const handleDownloadReport = async (e) => {
    e?.preventDefault?.();

    if (!fromDate || !toDate) {
      alert('Please select both From Date and To Date.');
      return;
    }

    setIsDownloading(true);

    try {
      const formattedFromDate = new Date(fromDate).toISOString();
      const formattedToDate = new Date(toDate).toISOString();

      const queryParams = new URLSearchParams();
      queryParams.append('vendorId', selectionData.vendor_id || '');
      queryParams.append('branchId', selectionData.branch_id || '');
      queryParams.append('fromDate', formattedFromDate);
      queryParams.append('toDate', formattedToDate);
      queryParams.append('session_year', sessionData.session_year);
      if (curr_class) queryParams.append('classId', curr_class);

      const response = await apiService.get(
        `/api/CommonMaster/GetFeeCollectionDateWiseReport?${queryParams.toString()}`,
        {
          responseType: 'blob',
          headers: { 'Content-Type': undefined },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FeeCollectionReport_${fromDate}_to_${toDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('Failed to download report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {/* ================================================= */}
      {/* VENDOR BRANCH SELECTOR                            */}
      {/* ================================================= */}

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      {/* ================================================= */}
      {/* REPORT CARD                                       */}
      {/* ================================================= */}

      <Card className="card-box mb-20">
        <CardTitle
          tag="h6"
          className="p-3 mb-0 border-bottom d-flex align-items-center"
        >
          <i className="bi bi-file-earmark-spreadsheet mr-2"></i>
          Fee Collection Date-Wise Report
        </CardTitle>

        <CardBody>

          {/* =============================================== */}
          {/* FILTERS ROW                                    */}
          {/* =============================================== */}

          <div className="row g-3 align-items-end mb-4">

            {/* FROM DATE */}
            <div className="col-12 col-md-4">
              <FormGroup className="mb-0">
                <Label for="fromDate" className="font-weight-bold">
                  From Date
                </Label>
                <Input
                  id="fromDate"
                  type="date"
                  value={fromDate}
                  min={sessionData.session_start_date}
                  max={getMaxDate(
                    sessionData.session_end_date,
                    sessionData.currentDate
                  )}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </FormGroup>
            </div>

            {/* TO DATE */}
            <div className="col-12 col-md-4">
              <FormGroup className="mb-0">
                <Label for="toDate" className="font-weight-bold">
                  To Date
                </Label>
                <Input
                  id="toDate"
                  type="date"
                  value={toDate}
                  min={sessionData.session_start_date}
                  max={getMaxDate(
                    sessionData.session_end_date,
                    sessionData.currentDate
                  )}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </FormGroup>
            </div>

            {/* CLASS FILTER */}
            <div className="col-12 col-md-4">
              <FormGroup className="mb-0">
                <Label for="curr_class" className="font-weight-bold">
                  Class
                </Label>
                <Input
                  id="curr_class"
                  className="custom-select"
                  type="select"
                  value={curr_class}
                  onChange={(e) => setcurr_class(e.target.value)}
                >
                  <option value="">— All Classes —</option>
                  {classMasterData.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.class_Name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </div>

            {/* DOWNLOAD BUTTON */}
            <div className="col-12 col-md-12">
              <Button
                color="success"
                block
                disabled={isDownloading}
                onClick={handleDownloadReport}
                className="d-flex align-items-center justify-content-center"
              >
                {isDownloading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Generating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-download mr-2" />
                    Download Report
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* =============================================== */}
          {/* INFO BOX                                       */}
          {/* =============================================== */}

          <div className="alert alert-info d-flex align-items-start mb-0" role="alert">
            <i className="bi bi-info-circle-fill mr-2 mt-1" />
            <div>
              <strong>Date-wise Fee Collection Report</strong>
              <br />
              Select a date range and optionally filter by class, then click
              <strong> Download Report</strong> to export the Excel file.
            </div>
          </div>

        </CardBody>
      </Card>

      {/* ================================================= */}
      {/* FULL SCREEN DOWNLOAD OVERLAY                      */}
      {/* ================================================= */}

      {isDownloading && (
        <div
          className="position-fixed d-flex flex-column align-items-center justify-content-center"
          style={{
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 9999,
          }}
        >
          <div className="bg-white rounded shadow p-4 text-center">
            <div
              className="spinner-border text-success mb-3"
              style={{ width: '3rem', height: '3rem' }}
              role="status"
            />
            <h5 className="mb-1">Generating Report</h5>
            <p className="text-muted mb-0">
              Please wait while your Excel file is being prepared...
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Fee_Collection_DateWise_Report;